// supervisor-release-flow.ts
//
// P7 §4/§8/§14: mission-level sequence run ONCE per mission after
// `runSupervisorUntilIdle` returns "done" (every ticket delivered/tested —
// see supervisor-mission-run.ts). This is NOT a per-ticket step; it moves
// the MISSION phase REVIEWING -> DEPLOYING -> VERIFYING -> DONE using
// mission-state-machine.ts's transition table, calling straight through to
// Agent E's already-built adapters (review-local/remote, draft-MR push,
// deployment, verification, rollback) — this module's own job is only
// SEQUENCING + gating each step behind the previous one's outcome, never
// reimplementing what those adapters already decide.
//
// Split into two files (≤200-line convention): this one covers
// REVIEWING -> REVIEW_REMOTE -> WAITING_GATE(G3); the deploy/verify/abort
// leg lives in supervisor-release-deploy-verify.ts.
import { randomUUID } from "node:crypto";
import { collectRemoteEvidenceWithoutThrowing } from "./collect-remote-evidence-without-throwing.js";
import { requestG3Gate } from "./deployment-adapter.js";
import type { PushDraftMergeRequestInput } from "./draft-merge-request-push.js";
import { deriveGateRequestId } from "./gate-registry.js";
import {
  isTerminalPhase,
  type MissionMachineState,
  transitionPhase,
} from "./mission-state-machine.js";
import type { MissionStore } from "./mission-store.js";
import { releaseCiGateOutcome, waitingForPipelineToAppear } from "./release-ci-gate.js";
import { pushOrReuseCandidate } from "./release-flow-candidate-push.js";
import { g3ArtifactHash } from "./release-manifest-hash.js";
import { checkRemoteEvidenceBoundToPushedCandidate } from "./remote-evidence-candidate-binding.js";
import type { WorkerReviewReport } from "./review-local-adapter.js";
import { controllerRecordReviewReceiptFromReport } from "./review-local-adapter.js";
import type {
  CollectRemoteEvidenceDeps,
  CollectRemoteEvidenceInput,
} from "./review-remote-adapter.js";
import type { ReleaseCiNotPassedOutcome } from "./supervisor-release-flow-resume-outcome.js";

export type ReleaseRisk = "low" | "medium" | "high" | "critical";

export interface ReleaseFlowUpToG3Input {
  store: MissionStore;
  controlRoot: string;
  missionId: string;
  repoDir: string;
  candidateSha: string;
  baseSha: string;
  policyDigest: string;
  adapterDigest: string;
  eventSeq: number;
  workerReviewReport: WorkerReviewReport;
  pushInput: Omit<
    PushDraftMergeRequestInput,
    "store" | "repoDir" | "candidateSha" | "baseSha" | "policyDigest" | "missionId"
  >;
  remoteEvidenceInput: Omit<CollectRemoteEvidenceInput, "expectedBaseSha" | "pushedSha">;
  remoteEvidenceDeps?: CollectRemoteEvidenceDeps;
  releaseManifestHash: string;
  /** Clock (ms) for the "pipeline not created yet" window; tests inject it. */
  nowMs?: () => number;
}

export type ReleaseFlowUpToG3Result =
  | {
      status: "waiting_gate_g3";
      phase: MissionMachineState;
      g3RequestId: string;
      mrPushStdout: string;
      remoteCiStatus: string;
      /** Round 3 M-C: the reused push's MR trailer disagrees with the deploy recipe configured now. */
      warning?: string;
    }
  | { status: "blocked_review_local"; reason: string }
  | { status: "blocked_push_policy"; reason: string }
  | { status: "push_failed"; reason: string }
  | { status: "resource_missing_remote_evidence"; reason: string }
  | { status: "review_evidence_sha_mismatch"; reason: string }
  /** The pushed commit's MR pipeline is not a pass (running/failed/manual/…): G3 NOT requested. */
  | { status: "ci_not_passed"; outcome: ReleaseCiNotPassedOutcome };

/**
 * REVIEWING -> (receipt) -> push Draft MR -> REVIEW_REMOTE -> G3 request.
 * Each step gates the next: no receipt => no push; push denied/failed =>
 * stop; missing PAT/host on remote evidence => `resource_missing` (caller
 * routes this to G1 amendment/WAITING_HUMAN per spec, NOT task_failed —
 * this function only reports the outcome, it does not itself decide the
 * mission's WAITING_HUMAN transition).
 */
export async function runReleaseFlowUpToG3(
  input: ReleaseFlowUpToG3Input,
): Promise<ReleaseFlowUpToG3Result> {
  let phase: MissionMachineState = { phase: "REVIEWING", executionStatus: "READY" };

  const reviewResult = await controllerRecordReviewReceiptFromReport({
    report: input.workerReviewReport,
    stagingReportPath: `${input.controlRoot}/review-staging/${input.candidateSha}.json`,
    controlRoot: input.controlRoot,
    missionId: input.missionId,
    repoPath: input.repoDir,
    candidateSha: input.candidateSha,
    baseSha: input.baseSha,
    policyDigest: input.policyDigest,
    adapterDigest: input.adapterDigest,
    eventSeq: input.eventSeq,
  });
  if (reviewResult.status === "blocked") {
    return { status: "blocked_review_local", reason: reviewResult.reason };
  }

  const nowMs = input.nowMs ?? Date.now;
  const push = await pushOrReuseCandidate(input, nowMs);
  if (push.status !== "ready") return push;

  phase = transitionPhase(phase, "DEPLOYING");
  // "DEPLOYING" here only marks that the mission has moved past REVIEWING
  // in the phase table — the ACTUAL deploy side effect only happens after
  // G3 approval (deploy-verify leg), never before. review_remote itself has
  // no dedicated phase slot in mission-state-machine.ts's table (spec §4
  // keeps it inside REVIEWING/DEPLOYING's boundary), so this call happens
  // between the two transitions rather than as its own phase.

  const remoteResult = await collectRemoteEvidenceWithoutThrowing(
    { ...input.remoteEvidenceInput, expectedBaseSha: push.baseSha, pushedSha: push.pushedSha },
    input.remoteEvidenceDeps,
  );
  // Round 3 M-B: after a reused push, say the push was not repeated (and the exits).
  const withNote = (reason: string) => (push.reuseNote ? `${reason} ${push.reuseNote}` : reason);
  if (remoteResult.kind !== "ok") {
    return { status: "resource_missing_remote_evidence", reason: withNote(remoteResult.reason) };
  }

  // X2 (B2-4): head, base AND pipeline must be the commit just pushed.
  const binding = checkRemoteEvidenceBoundToPushedCandidate(remoteResult.evidence, {
    pushedSha: push.pushedSha,
    baseSha: push.baseSha,
  });
  if (!binding.ok) {
    // Round 2 L6 + F-B12-1: GitLab creates the MR pipeline and diff_refs seconds AFTER the push.
    const appearing =
      binding.pipelineMissingForCommit || binding.mergeRequestDiffNotReady
        ? waitingForPipelineToAppear({
            pushedSha: push.pushedSha,
            recordedAtMs: push.recordedAtMs,
            nowMs: nowMs(),
          })
        : undefined;
    if (appearing !== undefined) {
      const outcome = binding.mergeRequestDiffNotReady
        ? { ...appearing, reason: binding.reason }
        : appearing;
      return { status: "ci_not_passed", outcome };
    }
    return { status: "review_evidence_sha_mismatch", reason: withNote(binding.reason) };
  }
  // CI gate (dogfood 2026-10-01): the pipeline X2 just bound to the pushed
  // commit must have PASSED before G3 is asked — a running one used to be
  // approved 4 s after the push and failed later. A re-run reuses the push the
  // journal records for this candidate (release-flow-candidate-push.ts) and re-checks.
  const ciGate = releaseCiGateOutcome(remoteResult.evidence);
  if (ciGate !== undefined) return { status: "ci_not_passed", outcome: ciGate };

  const g3RequestId = deriveGateRequestId(
    "g3",
    g3ArtifactHash(input.candidateSha, input.releaseManifestHash),
  );
  const g3Request = await requestG3Gate({
    controlRoot: input.controlRoot,
    candidateSha: input.candidateSha,
    releaseManifestHash: input.releaseManifestHash,
    requestId: g3RequestId,
  });
  if (g3Request.status === "rejected") {
    return { status: "blocked_push_policy", reason: g3Request.reason };
  }

  await input.store.commit([
    {
      event_id: randomUUID(),
      delta: {
        kind: "operation_intent",
        operation_id: `release-flow-g3-wait-${input.missionId}`,
        operation_kind: "waiting_gate_g3",
        params: { request_id: g3RequestId, candidate_sha: input.candidateSha },
      },
    },
  ]);

  return {
    status: "waiting_gate_g3",
    phase,
    g3RequestId,
    mrPushStdout: push.stdout,
    remoteCiStatus: remoteResult.evidence.ciStatus,
    ...(push.warning === undefined ? {} : { warning: push.warning }),
  };
}

export function isReleaseFlowTerminal(phase: MissionMachineState): boolean {
  return isTerminalPhase(phase.phase);
}
